import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ExpenseDialog, type ExpenseRow } from "./ExpenseDialog";

const fmt = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

const CATEGORY_LABEL: Record<string, string> = {
  hosting: "Hosting / Cloud",
  ai_api: "AI / API",
  contractors: "Contractors",
  salaries: "Salaries",
  tools_saas: "Tools & SaaS",
  marketing: "Marketing",
  legal_accounting: "Legal & Accounting",
  taxes: "Taxes",
  refunds_manual: "Refunds (manual)",
  other: "Other",
};

export const ExpensesPanel = ({ environment, onChanged }: { environment: "sandbox" | "live"; onChanged?: () => void }) => {
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("operating_expenses")
      .select("id, occurred_on, category, amount_cents, currency, vendor, note, frequency, environment")
      .eq("environment", environment)
      .is("deleted_at", null)
      .order("occurred_on", { ascending: false })
      .limit(500);
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    setRows((data ?? []) as ExpenseRow[]);
  }, [environment]);

  useEffect(() => { void load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this expense?")) return;
    const { error } = await supabase
      .from("operating_expenses")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Expense deleted");
    void load();
    onChanged?.();
  };

  const total = useMemo(() => rows.reduce((s, r) => s + r.amount_cents, 0), [rows]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Operating expenses</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {rows.length} entries · {fmt(total)} face value (recurring rows are amortized in P&L)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4" /> Add expense
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-sm">No expenses yet.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.occurred_on}</TableCell>
                <TableCell>{CATEGORY_LABEL[r.category] ?? r.category}</TableCell>
                <TableCell className="text-muted-foreground">{r.vendor || "—"}</TableCell>
                <TableCell><Badge variant="secondary">{r.frequency}</Badge></TableCell>
                <TableCell className="text-right font-mono">{fmt(r.amount_cents)}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="icon" variant="ghost" aria-label="Edit expense" onClick={() => { setEditing(r); setDialogOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" aria-label="Delete expense" onClick={() => handleDelete(r.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ExpenseDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          environment={environment}
          existing={editing}
          onSaved={() => { void load(); onChanged?.(); }}
        />
      </CardContent>
    </Card>
  );
};
